import React, { useCallback } from 'react';
import { padEnd } from 'lodash';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import Button from 'components/Button';
import { IFocusableParentProps } from 'components/navigation/FocusableParent';
import {
  FocusableActionsWrapper,
  StyledButtonWrapper,
  StyledButtonText,
  StyledInputWrapper,
  StyledInput,
  StyledInputCursor,
  StyledInputText,
  StyledInputInvalidMessage,
  StyledPinInputChar,
} from './FormStyledComponents';

interface IActionsButton {
  onEnter: () => void;
  onFocus?: () => void;
  label: React.ReactNode;
  focusOnMouseEnter?: boolean;
  disabled?: boolean;
}

const ActionsButton: React.FC<IActionsButton> = ({
  onEnter,
  onFocus,
  label,
  focusOnMouseEnter,
  disabled,
}) => (
  <StyledButtonWrapper>
    <Button
      id="actions-button"
      onEnter={onEnter}
      onFocus={onFocus}
      focusOnMouseEnter={focusOnMouseEnter}
      disabled={disabled}
    >
      <StyledButtonText>{label}</StyledButtonText>
    </Button>
  </StyledButtonWrapper>
);

ActionsButton.propTypes = {
  onEnter: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  focusOnMouseEnter: PropTypes.bool,
  disabled: PropTypes.bool,
};

interface IActionsLink {
  onFocus?: () => void;
  label: React.ReactNode;
  to: string;
  focusOnMouseEnter?: boolean;
  disabled?: boolean;
}

const ActionsLink: React.FC<IActionsLink> = ({
  onFocus,
  label,
  to,
  focusOnMouseEnter = true,
  disabled,
}) => {
  const history = useHistory();
  const onEnter = useCallback(() => history.push(to), [history, to]);

  return (
    <StyledButtonWrapper>
      <Button
        id="actions-link"
        onEnter={onEnter}
        onFocus={onFocus}
        focusOnMouseEnter={focusOnMouseEnter}
        disabled={disabled}
      >
        <StyledButtonText>{label}</StyledButtonText>
      </Button>
    </StyledButtonWrapper>
  );
};

ActionsLink.propTypes = {
  to: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  onFocus: PropTypes.func,
  focusOnMouseEnter: PropTypes.bool,
  disabled: PropTypes.bool,
};

interface IActionsInput {
  onEnter: () => void;
  onFocus?: () => void;
  value?: string | JSX.Element[];
  placeholder?: string | React.ReactNode;
  isValid?: boolean;
  invalidMessage?: string;
  cursor?: boolean;
  cursorPosition?: number;
  centeredText?: boolean;
}

const ActionsInput: React.FC<IActionsInput> = ({
  onEnter,
  onFocus,
  value,
  placeholder,
  isValid = true,
  invalidMessage,
  cursor = false,
  cursorPosition = 0,
  centeredText = false,
}) => {
  let valueComponent;
  const cursorComponent = <StyledInputCursor key="cursor" />;

  if (cursor && value) {
    const valueWithCursorArray = [
      ...value.slice(0, cursorPosition),
      cursorComponent,
      ...value.slice(cursorPosition),
    ];
    valueComponent = (
      <StyledInputText centeredText={centeredText} isActive>
        {valueWithCursorArray}
      </StyledInputText>
    );
  } else {
    valueComponent = (
      <StyledInputText centeredText={centeredText}>
        {cursor && cursorComponent}
        {value || placeholder}
      </StyledInputText>
    );
  }

  return (
    <StyledInputWrapper>
      <StyledInput id="actions-input" onEnter={onEnter} onFocus={onFocus} isValid={isValid}>
        {valueComponent}
      </StyledInput>
      {!isValid && <StyledInputInvalidMessage>{invalidMessage}</StyledInputInvalidMessage>}
    </StyledInputWrapper>
  );
};

ActionsInput.propTypes = {
  onEnter: PropTypes.func,
  onFocus: PropTypes.func,
  isValid: PropTypes.bool,
  invalidMessage: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  cursor: PropTypes.bool,
  cursorPosition: PropTypes.number,
};

interface IActionsPinInput extends IActionsInput {
  value: string;
}

const MAX_PIN_LENGTH = 4;

const ActionsPinInput: React.FC<IActionsPinInput> = ({ value, ...rest }) => {
  const cursorPosition = value.length;
  const preparedValue = [...padEnd(value, MAX_PIN_LENGTH, '_')].map((char, i) => {
    const isPlaceholder = char === '_';
    const isActive = i === value.length;

    return (
      <StyledPinInputChar key={i} isActive={isActive} isPlaceholder={isPlaceholder}>
        {char}
      </StyledPinInputChar>
    );
  });
  return (
    <ActionsInput value={preparedValue} cursorPosition={cursorPosition} centeredText {...rest} />
  );
};

type StaticComponents = {
  Button: React.FC<IActionsButton>;
  Link: React.FC<IActionsLink>;
  Input: React.FC<IActionsInput>;
  PinInput: React.FC<IActionsPinInput>;
};

const ActionsSection: React.FC<IFocusableParentProps> & StaticComponents = (props) => (
  <FocusableActionsWrapper {...props} />
);

ActionsSection.Button = ActionsButton;
ActionsSection.Link = ActionsLink;
ActionsSection.Input = ActionsInput;
ActionsSection.PinInput = ActionsPinInput;

export default ActionsSection;
